/**
 * Pre-Lab simulation logic.
 *
 * Given a valve opening percentage, computes the steady-state operating
 * point of the pump test stand and returns simulated gauge readings
 * (suction pressure, discharge pressure, flow rate) with optional noise.
 *
 * Noise is seeded per valve position so readings are deterministic for
 * a given opening regardless of the order positions are visited.
 */

import { pumpTestStand as ts } from "@/data/pumpTestStand.js";
import {
  pipeArea,
  reynoldsNumber,
  frictionFactor
} from "@/utils/simulation.js";
import seedrandom from "seedrandom";
import config from "@/data/config.json";
import { M_TO_FT, M3S_TO_GPM, KPA_TO_PSI } from "@/utils/units.js";

const G = 9.81;
const BISECT_ITER = 60;
const BISECT_TOL = 1e-9; // m³/s — flow interval width stop condition
const HEAD_TOL = 1e-6; // m    — head residual stop condition

/* ────────────── valve model ────────────── */

/**
 * Valve minor-loss coefficient from equal-percentage characteristic.
 * K(x) = Kopen · R^(2·(1 − x))   where x = opening / 100
 */
export function valveK(openingPercent) {
  if (openingPercent <= 0) return Infinity;
  const x = Math.min(openingPercent, 100) / 100;
  return ts.valve.Kopen * Math.pow(ts.valve.rangeability, 2 * (1 - x));
}

/* ────────────── head calculations ────────────── */

/** Pump head at flow Q. */
export function pumpHead(Q) {
  return ts.trueCurve.a - ts.trueCurve.b * Q * Q;
}

/**
 * Total system head loss (closed loop, net static head = 0).
 * Includes friction in suction + discharge pipe, all fitting K-values,
 * and the variable valve K.
 */
function systemHeadLoss(Q, Kvalve) {
  const D = ts.pipe.diameter_m;
  const eps = ts.pipe.roughness_m;
  const rho = ts.fluid.density_kgm3;
  const mu = ts.fluid.viscosity_Pas;
  const L = ts.suction.length_m + ts.discharge.length_m;
  const Ktotal = ts.suction.K + ts.discharge.K + Kvalve;

  const A = pipeArea(D);
  const v = Q / A;
  if (v <= 0) return 0;

  const Re = reynoldsNumber(rho, v, D, mu);
  const f = frictionFactor(Re, eps, D);

  const hf = (f * (L / D) * (v * v)) / (2 * G);
  const hm = (Ktotal * (v * v)) / (2 * G);
  return hf + hm;
}

/* ────────────── operating-point solver ────────────── */

/**
 * Find the steady-state flow rate by bisecting Hp(Q) − Hs(Q) = 0.
 */
export function findOperatingQ(openingPercent) {
  if (openingPercent <= 0) return 0;
  const Kv = valveK(openingPercent);
  const Qmax = Math.sqrt(ts.trueCurve.a / ts.trueCurve.b);

  let lo = 0;
  let hi = Qmax;

  for (let i = 0; i < BISECT_ITER; i++) {
    const mid = (lo + hi) / 2;
    const diff = pumpHead(mid) - systemHeadLoss(mid, Kv);
    if (Math.abs(diff) < HEAD_TOL || hi - lo < BISECT_TOL) return mid;
    if (diff > 0) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/* ────────────── gauge pressures ────────────── */

/**
 * Suction and discharge gauge pressures (Pa, gauge) at flow Q.
 *
 * Suction gauge — open to atmosphere at the pump inlet: P_s = 0 Pa gauge.
 * (I-1 transducer is constant, vented to atm per the P&ID.)
 *
 * Discharge gauge — Bernoulli across the pump:
 *   P_d = ρ · g · Hp
 */
function gaugeReadings(Q) {
  const rho = ts.fluid.density_kgm3;

  // Suction side is open to atmosphere → P_s = 0 gauge
  const Ps_Pa = 0;

  const Hp = pumpHead(Q);
  const Pd_Pa = Ps_Pa + rho * G * Hp;

  return { Ps_Pa, Pd_Pa };
}

/* ────────────── deterministic noise ────────────── */

/**
 * Gaussian noise samples seeded by valve position so results are
 * reproducible regardless of visit order.
 */
function makeNoise(openingPercent) {
  const seed = config.settings.randomSeed + "_prelab_" + openingPercent;
  const rng = seedrandom(seed);
  // Box-Muller transform → 3 independent normal variates
  const u1 = rng(),
    u2 = rng();
  const u3 = rng(),
    u4 = rng();
  const u5 = rng(),
    u6 = rng();
  return {
    n1: Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2),
    n2: Math.sqrt(-2 * Math.log(u3)) * Math.cos(2 * Math.PI * u4),
    n3: Math.sqrt(-2 * Math.log(u5)) * Math.cos(2 * Math.PI * u6)
  };
}

/* ────────────── public API ────────────── */

/**
 * Simulated test-stand readings for a given valve opening.
 *
 * @param {number} openingPercent  0–100
 * @param {boolean} addNoise       add gauge noise (default true)
 * @returns {{ valveOpening, flowRate_m3s, flowRate_GPM,
 *             suctionPressure_kPa, dischargePressure_kPa,
 *             suctionPressure_psi, dischargePressure_psi,
 *             _trueQ_m3s, _trueHead_m, _trueHead_ft }}
 */
export function getTestStandReadings(openingPercent, addNoise = true) {
  const Q = findOperatingQ(openingPercent);
  const { Ps_Pa, Pd_Pa } = gaugeReadings(Q);

  let Ps_kPa = Ps_Pa / 1000;
  let Pd_kPa = Pd_Pa / 1000;
  let Q_display = Q;

  if (addNoise && openingPercent > 0) {
    const { n1, n2, n3 } = makeNoise(openingPercent);
    Ps_kPa += n1 * ts.noise.pressure_kPa;
    Pd_kPa += n2 * ts.noise.pressure_kPa;
    Q_display = Math.max(0, Q + n3 * ts.noise.flow_fraction * Q);
  }

  const head_m = pumpHead(Q);

  return {
    valveOpening: openingPercent,
    flowRate_m3s: Q_display,
    flowRate_GPM: Q_display * M3S_TO_GPM,
    suctionPressure_kPa: Ps_kPa,
    dischargePressure_kPa: Pd_kPa,
    suctionPressure_psi: Ps_kPa * KPA_TO_PSI,
    dischargePressure_psi: Pd_kPa * KPA_TO_PSI,
    _trueQ_m3s: Q,
    _trueHead_m: head_m,
    _trueHead_ft: head_m * M_TO_FT
  };
}
